import { AdminJobTopActionButton } from '@components/core-ui/form-elements/buttons/Button/variations/AdminJobTopActionButton';
import EyeClosed from '@components/icons/EyeClosed.svg';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { useMutation } from '@tanstack/react-query';
import { AdminService } from 'app/API';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import toast from 'react-hot-toast';
import { usePageContext } from '../../../../../../providers/PageProvider';

export const AbandonedProcessingButton = () => {
  const { call } = useAPI();
  const { openModal, closeModal } = useModalContext();
  const { job, manualReload } = usePageContext();

  const updateMutation = useMutation({
    mutationFn: ({ jobId, abandonedProcessing }: { jobId: string; abandonedProcessing: boolean }) =>
      call(() => AdminService.updateJobAbandonedProcessing({ jobId, requestBody: { abandonedProcessing } })),
    onSuccess: (_response, { abandonedProcessing }) => {
      toast.success(
        abandonedProcessing ? 'Job marked as abandoned processing' : 'Job removed from abandoned processing',
      );
      manualReload();
    },
    onError: () => {
      toast.error('Unable to update abandoned processing flag');
    },
  });
  const isUpdating = updateMutation.isPending;

  if (!job) {
    return null;
  }

  const isAbandoned = job.abandonedProcessing;

  const confirmUpdate = () => {
    openModal(ModalNamesEnum.GenericConfirmation, {
      title: isAbandoned ? 'Remove abandoned processing flag' : 'Mark job as abandoned processing',
      description: isAbandoned
        ? 'This job will be included again in stuck, delayed, and oldest-open processing reports.'
        : 'This job will be excluded from stuck, delayed, and oldest-open processing reports. Its status, billing, and visibility are unchanged.',
      buttonText: isAbandoned ? 'Remove flag' : 'Mark abandoned',
      onCancel: () => closeModal(),
      onSuccess: () => {
        closeModal();
        updateMutation.mutate({ jobId: job.id, abandonedProcessing: !isAbandoned });
      },
    });
  };

  const label = isAbandoned ? 'Unmark abandoned' : 'Mark abandoned';

  return (
    <AdminJobTopActionButton
      role="button"
      aria-label={label}
      aria-disabled={isUpdating}
      onClick={isUpdating ? undefined : confirmUpdate}
      className={`min-w-[120px] ${isUpdating ? 'cursor-not-allowed opacity-60' : ''}`}
      title={label}
    >
      <EyeClosed />
      {isUpdating ? 'Saving…' : label}
    </AdminJobTopActionButton>
  );
};
